import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { ButtonSpinnerSlot } from './ui/ButtonSpinner';
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import PaymentComponent from "./PaymentComponent";
import PackageDialog from "./course/packagedialog";
import Today from './dashboard/today'
import { motion, useInView } from "framer-motion";
import styled from "styled-components";
import AssessmentDashboard from "./dashboard/assessmentDashboard";
import MandatoryPackageDialog from "./MandatoryPackageDialog";
import PlanExpiredNoticeDialog from "./PlanExpiredNoticeDialog";
import { useStripeConnectSetup } from "./stripeConnectSetup";
import DashboardNavbar from './dashboardNavbar';
import { useThemeMode } from './ui/themeMode';
import {
  IconBtn, Btn, Seg, StatGrid, Stat, StatTop, StatIco, StatDelta, StatLabel,
  StatValue, StatBar, StatMeta, ChartGrid, Card, SectionHead, Legend, LegendRow,
  LegendSw, Tag, Av, Muted, RowBetween, Skeleton,
} from './ui/primitives';
import { Icon, Donut, StackBars } from './ui/shell';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Toggle from './ui/Toggle';
import { LabelWithHelp } from './ui/FieldHelp';
import { DASHBOARD_KPI_HELP } from './ui/kpiFieldHelp';
import { Sun, Moon, X, Lock } from 'lucide-react';
import { useCourseList } from './hooks/useCourseList';
import { getPortalDisplayName } from './impersonationView';
import { useAppContext } from '../appprovider';
import { canAccessBilling, isNonOwnerOrgStaff } from './services/instructorPortalAccess';
import useDemoActionGate from './hooks/useDemoActionGate';
import DemoLockedDialog from './ui/DemoLockedDialog';
import {
  cachePackageLimits,
  planLimitsFromEntitlements,
  canShowStripeConnect,
} from './services/instructorMeterLimits';
import { getCache, setCache } from './ui/listCache';
import {
  buildDashboardCountsCacheKey,
  buildDashboardResultsCacheKey,
  buildDashboardSummaryCacheKey,
  fetchDashboardCounts,
  fetchDashboardResults,
  fetchDashboardSummary,
  mapDashboardActivity,
  mapDashboardTrend,
} from './dashboard/dashboardApi';

const AV_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#0ea5e9', '#8b5cf6', '#14b8a6', '#ef4444'];

// timestamp (ms or numeric string) -> relative "x ago"
const relTime = (ts) => {
  const t = typeof ts === 'string' ? parseInt(ts, 10) : ts;
  if (!t || isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Build a 6-month [{ m, v:[submissions, passed] }] trend from result rows.
const buildTrend = (rows) => {
  const now = new Date();
  const buckets = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, m: d.toLocaleDateString('en-US', { month: 'short' }), v: [0, 0] });
  }
  const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
  rows.forEach((r) => {
    const t = typeof r.createdAt === 'string' ? parseInt(r.createdAt, 10) : r.createdAt;
    if (!t || isNaN(t)) return;
    const d = new Date(t);
    const k = `${d.getFullYear()}-${d.getMonth()}`;
    if (idx[k] == null) return;
    const b = buckets[idx[k]];
    b.v[0] += 1; // submission
    const pct = r.totalMarks && Number(r.totalMarks) > 0 ? (Number(r.score) / Number(r.totalMarks)) * 100 : 0;
    if (pct >= 50) b.v[1] += 1; // passed
  });
  return buckets.map(({ m, v }) => ({ m, v }));
};

// dd-mm-yyyy; handles epoch timestamps (ms, number or numeric string) and ISO strings.
const initials = (n) => String(n || 'U').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const ConWrapper = styled.div`
  display: block;
  height: auto;
  min-height: 0;
`;

const ContainerDashboard = styled.div`
  width: 100%;
  max-height: none;
  overflow: visible;
  margin: 0;
`;

const StatGrid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.gap};

  @media (max-width: 980px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const DashboardContainer = styled.div`
  font-family: ${({ theme }) => theme.fontBody};
  color: ${({ theme }) => theme.text};
  max-width: 1240px;
  margin: 0 auto;
  animation: fadeIn 0.28s cubic-bezier(0.2, 0.6, 0.2, 1);

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StripeSetupBanner = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  margin-bottom: ${({ theme }) => theme.gap};
  border-radius: ${({ theme }) => theme.rLg};
  background: color-mix(in oklch, ${({ theme }) => theme.warning} 10%, ${({ theme }) => theme.surface});
  border: 1px solid color-mix(in oklch, ${({ theme }) => theme.warning} 22%, ${({ theme }) => theme.border});

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StripeBannerBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const StripeBannerMessage = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.text};
  line-height: 1.5;
`;

const StripeBannerToggleRow = styled.div`
  margin-top: 10px;
`;

const StripeBannerActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const DashboardHero = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.gap};
  margin-bottom: ${({ theme }) => theme.gap};

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const HeroEyebrow = styled.div`
  color: ${({ theme }) => theme.textSubtle};
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h2`
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 22px;
  font-weight: 760;
  letter-spacing: -.02em;
  margin: 0;
`;

const HeroText = styled.p`
  color: ${({ theme }) => theme.textMuted};
  font-size: ${({ theme }) => theme.fsSm};
  margin: 4px 0 0;
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

/* Hero buttons: smaller text, Hanken, white label. */
const HeroBtn = styled(Btn)`
  font-family: ${({ theme }) => theme.fontBody};
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 650;
  letter-spacing: .01em;
  color: #fff;
  svg { width: 14px; height: 14px; }
`;

const FlexRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({ theme }) => theme.gap};

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StyledCard = styled.div`
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.rLg};
  box-shadow: ${({ theme }) => theme.cardShadow};
  padding: ${({ theme }) => theme.padCard};
  transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.18s, border-color 0.18s;

  &:hover {
    border-color: ${({ theme }) => theme.border};
    box-shadow: ${({ theme }) => theme.ring1}, ${({ theme }) => theme.shadowLg};
    transform: translateY(-2px);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StatIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accentStrong};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.accentLine};
  font-size: 13px;
  font-weight: 800;
`;

const StatChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 11px;
  border-radius: ${({ theme }) => theme.rPill};
  background: ${({ theme }) => theme.surface3};
  color: ${({ theme }) => theme.textMuted};
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 650;
`;

const Title = styled.h5`
  color: ${({ theme }) => theme.textMuted};
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 600;
  margin: 16px 0 0;
`;

const Value = styled.h3`
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 30px;
  font-weight: 760;
  letter-spacing: -0.02em;
  line-height: 1;
  margin: 6px 0 0;

  span {
    color: ${({ theme }) => theme.textSubtle};
    font-size: 15px;
    font-weight: 650;
  }
`;

const ProgressTrack = styled.div`
  height: 6px;
  border-radius: 4px;
  background: ${({ theme }) => theme.surface3};
  margin-top: 14px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(17, 20, 38, 0.04);

  i {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, ${({ theme }) => theme.accent}, ${({ theme }) => theme.accentStrong});
  }
`;

const ChartTitle = styled.h6`
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 4px;
`;

const PieCard = styled(StyledCard)`
  min-height: 330px;
`;

const ResponsiveContainerWrapper = styled.div`
  width: 100%;
  height: 260px;
  display: flex;
  margin-top: 12px;
  overflow: visible;

  svg text {
    fill: ${({ theme }) => theme.text};
    font-size: 11px;
    font-weight: 700;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    height: 220px;
  }
`;

const ChartHint = styled.p`
  color: ${({ theme }) => theme.textMuted};
  font-size: ${({ theme }) => theme.fsSm};
  margin: 0;
`;

const DashboardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
  max-width: 1240px;
  margin: ${({ theme }) => theme.gap} auto 0;
`;

const PanelFrame = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rLg};
  box-shadow: ${({ theme }) => theme.shadowMd};
  padding: ${({ theme }) => theme.padCard};
  transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.18s;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadowLg};
    transform: translateY(-2px);
  }
`;


export default function Content() {       
  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem('userId');
  const packageId = localStorage.getItem('packageDetails');
  const price = localStorage.getItem('price');
  const email = localStorage.getItem('email');
  const username = getPortalDisplayName();
  const { mode, toggleModeFromEvent } = useThemeMode();
  const theme = useTheme();
  const { courses } = useCourseList();
  const { entitlements, loadEntitlements } = useAppContext();
  const { isLocked, guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();

  // Safety net: never leave shimmers up indefinitely.
  useEffect(() => {
    const statsTimer = setTimeout(() => setStatsLoading(false), 15000);
    const resultsTimer = setTimeout(() => setResultsLoading(false), 15000);
    return () => {
      clearTimeout(statsTimer);
      clearTimeout(resultsTimer);
    };
  }, []);
  const navigate = useNavigate();
  const [period, setPeriod] = useState('This term');
  const [statsLoading, setStatsLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [courseCount, setCourseCount] = useState(0);
  const [assessmentCount, setAssessmentCount] = useState(0);
  const [studentCount, setStudentCount] = useState(0);
  const [totalCourseCount, setTotalCourseCount] = useState(0);
  const [totalAssessmentCount, setTotalAssessmentCount] = useState(0);
  const [totalStudentCount, setTotalStudentCount] = useState(0);
  const [assessmentList, setAssessmentList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [resultsMap, setResultsMap] = useState({});
  const [dashboardSummary, setDashboardSummary] = useState(null);
  // Recent-activity filters (role / course / free-text user-or-assessment).
  const [activityRole, setActivityRole] = useState('all');
  const [activityCourse, setActivityCourse] = useState('all');
  const [activitySearch, setActivitySearch] = useState('');
  // Recent-activity time window in days ('all' = no limit).
  const [activityWindow, setActivityWindow] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
 const [snackbarMessage, setSnackbarMessage] = useState("");
 const [expirationDate,setExpirationDate] = useState("");
 const [subscriptionId,setSubscriptionId] = useState("");
 const [price1,setPrice1] = useState("");
 const [mandatoryPackageDialog, setMandatoryPackageDialog] = useState(false);
 const userType = localStorage.getItem("userType");
 const stripeAccountId = localStorage.getItem('stripeAccountId')
   const [dontShowAgain, setDontShowAgain] = useState(false);

 const menuItems = [
];
 
  useEffect(() => {
    if (userId && !entitlements) {
      loadEntitlements(userId);
    }
  }, [userId, entitlements, loadEntitlements]);

  useEffect(() => {
    if (!entitlements?.meters) return;
    const limits = planLimitsFromEntitlements(entitlements);
    setTotalCourseCount(limits.numberOfCourses ?? 0);
    setTotalStudentCount(limits.numberOfStudents ?? 0);
    setTotalAssessmentCount(limits.numberOfAssessments ?? 0);
    setUserPlan({
      numberOfCourses: limits.numberOfCourses,
      numberOfStudents: limits.numberOfStudents,
      numberOfAssessments: limits.numberOfAssessments,
      packageDetails: { name: limits.tier },
    });
    cachePackageLimits(limits);
  }, [entitlements]);

  // Data and Colors
  const colors = ["oklch(0.55 0.21 274)", "oklch(0.96 0.006 260)"];

  // Data for Pie Charts
  const dataSets = [
    [
      { name: "Course Created", value: courseCount },
      { name: "Course Left", value: totalCourseCount - courseCount },
    ],
    [
      { name: "Assessment Created", value: assessmentCount },
      { name: "Assessment Left", value: totalAssessmentCount - assessmentCount },
    ],
    [
      { name: "Created", value: studentCount },
      { name: "Remaining", value: totalStudentCount - studentCount },
    ],
  ];

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
    if (!dashboardSummary && !statsLoading) {
      setCourseCount(courses.length);
    }
  }, [courses, dashboardSummary, statsLoading]);

     useEffect(() => {
    const savedPreference = localStorage.getItem('hideStripeAlert');
    if (savedPreference === 'true') {
      setDontShowAgain(true);
    }
  }, []);

  // Handle checkbox change and persist to localStorage
  const handleCheckboxChange = (event) => {
    if (!guardAction('action.dashboard.hideStripeAlert')) return;
    const checked = event.target.checked;
    setDontShowAgain(checked);
    localStorage.setItem('hideStripeAlert', checked);
  };

  // Open dialog box
  const handleDialogOpen = () => {
    guardAction('action.dashboard.upgradePlan', () => setDialogOpen(true));
  };
  
  const handleSnackbarClose = () => {
    if (!guardAction('action.dashboard.hideStripeAlert')) return;
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const { isLoading, handleStripeOnboarding } = useStripeConnectSetup({
    userId,
    onMissingAccount: handleSnackbarOpen,
  });

  useEffect(() => {
    async function checkForPackage() {
      // Start dashboard metrics immediately — do not block on userplans / packages.
      const dashboardLoadPromise = fetchData();

      try {
        const responseUserPlan = await fetch(`${apiUrl}/api/userplans/${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
          },
        });
        if (!responseUserPlan.ok) {
          throw new Error(`HTTP error! Status: ${responseUserPlan.status}`);
        }
        const responseBody = await responseUserPlan.text();
        if (!responseBody) {
          if (!packageId || packageId === '1') {
            //if (packageId === '1') { if a new user sign up then automatically he will be added to trial package
              const lastPlanDate = new Date(); // Today's date
              const nextPlanDate = new Date();
              nextPlanDate.setDate(nextPlanDate.getDate() + 30);
              try {
                const body = {
                  userId: userId,
                  planId: '1',
                  price: 0,
                  lastPlanDate: lastPlanDate.toISOString(), // Convert to ISO string format
                  nextPlanDate: nextPlanDate.toISOString(),
                  subscriptionId: "",
                };
                const url = `${apiUrl}/api/userplans/${userId}`;
                const res = await axios.post(url, body, {
                  headers: {
                      'Content-Type': "application/json",
                  "Accept": "*/*",
                  "Access-Control-Allow-Origin": "*",
                  'Authorization': getBasicAuthHeader(),
                  },
                });
                localStorage.setItem('packageDetails', '1');
                localStorage.setItem('price', 0);
                if (res.status === 200) {
                  //console.log('User plan updated successfully: Trial');
                  window.location.reload();
                }
              } catch (error) {
                if (error.response && error.response.status === 409) {
                  //console.log('Conflict: User plan already exists');
                } else {
                  //console.error('An error occurred while posting the user plan:', error);
                }
              }
            //}
          } else {
            const pri = price;
            localStorage.setItem('packageIdplan', packageId);
            localStorage.setItem('packagePrice', pri);
            localStorage.removeItem('packageDetails');
            if(price===0)
            {
              const lastPlanDate = new Date(); // Today's date
              const nextPlanDate = new Date();
              nextPlanDate.setDate(nextPlanDate.getDate() + 30);
              try {
                const body = {
                  userId: userId,
                  planId: packageId,
                  price: 0,
                  lastPlanDate: lastPlanDate.toISOString(), // Convert to ISO string format
                  nextPlanDate: nextPlanDate.toISOString(),
                  subscriptionId: "",
                };
                const url = `${apiUrl}/api/userplans/${userId}`;
                const res = await axios.post(url, body, {
                  headers: {
                      'Content-Type': "application/json",
                  "Accept": "*/*",
                  "Access-Control-Allow-Origin": "*",
                  'Authorization': getBasicAuthHeader(),
                  },
                });
                localStorage.setItem('packageDetails', packageId);
                localStorage.setItem('price', 0);
                if (res.status === 200) {
                  //console.log('User plan updated successfully');
                  window.location.reload();
                }
              } catch (error) {
                if (error.response && error.response.status === 409) {
                  //console.log('Conflict: User plan already exists');
                } else {
                  //console.error('An error occurred while posting the user plan:', error);
                }
              }
            }
            else{
            const paymentComponent = new PaymentComponent();
            paymentComponent.makePayment(packageId, 'monthly');
            }
          }
        } else {
          const data = JSON.parse(responseBody);
          if (packageId && packageId > data.planId) {
            const pri = price;
            localStorage.setItem('packageIdplan', packageId);
            localStorage.setItem('packagePrice', pri);
            localStorage.removeItem('packageDetails');
            if(price===0)
              {
                const lastPlanDate = new Date(); // Today's date
                const nextPlanDate = new Date();
                nextPlanDate.setDate(nextPlanDate.getDate() + 30);
                try {
                  const body = {
                    userId: userId,
                    planId: packageId,
                    price: 0,
                    lastPlanDate: lastPlanDate.toISOString(), // Convert to ISO string format
                    nextPlanDate: nextPlanDate.toISOString(),
                    subscriptionId: "",
                  };
                  const url = `${apiUrl}/api/userplans/${userId}`;
                  const res = await axios.post(url, body, {
                    headers: {
                        'Content-Type': "application/json",
                    "Accept": "*/*",
                    "Access-Control-Allow-Origin": "*",
                    'Authorization': getBasicAuthHeader(),
                    },
                  });
                  localStorage.setItem('packageDetails', packageId);
                  localStorage.setItem('price', 0);
                  if (res.status === 200) {
                    //console.log('User plan updated successfully');
                    window.location.reload();
                  }
                } catch (error) {
                  if (error.response && error.response.status === 409) {
                    //console.log('Conflict: User plan already exists');
                  } else {
                    //console.error('An error occurred while posting the user plan:', error);
                  }
                }
              }
              else{
            const paymentComponent = new PaymentComponent();
            paymentComponent.makePayment(packageId, 'monthly');
              }
          } else {
            localStorage.setItem('packageDetails', data.planId);
            localStorage.setItem('price', data.price);
            setExpirationDate(data.nextPlanDate);
            setSubscriptionId(data.subscriptionId);
            setPrice1(data.price);
            await dashboardLoadPromise;
          }
        }
      } catch (error) {
        //console.error("Error in checkForPackage:", error);
        setStatsLoading(false);
        await dashboardLoadPromise.catch(() => {});
      }
    }

    function applyDashboardCounts(counts) {
      setAssessmentCount(counts.assessmentCount);
      setAssessmentList(counts.assessmentList);
      setStudentCount(counts.studentCount);
      setStudentList(counts.studentList);
      setTrainings(counts.trainings);
    }

    async function loadDashboardCounts() {
      const countsKey = buildDashboardCountsCacheKey(userId);
      const cachedCounts = getCache(countsKey);
      if (cachedCounts) return cachedCounts;

      const counts = await fetchDashboardCounts(apiUrl, userId, getBasicAuthHeader());
      setCache(countsKey, counts);
      return counts;
    }

    function applyDashboardSummary(summary) {
      setDashboardSummary(summary);
      setCourseCount(summary.courseCount ?? 0);
      setAssessmentCount(summary.assessmentCount ?? 0);
      setStudentCount(summary.studentCount ?? 0);

      // Plan limits: entitlements API is the source of truth (instructor_plan_entitlement).
      // summary.planLimits is kept for backward compatibility but must not overwrite entitlements.
      // const limits = summary.planLimits;
      // if (limits) { ... }
    }

    async function tryFetchDashboardSummary() {
      const summaryKey = buildDashboardSummaryCacheKey(userId);
      const cachedSummary = getCache(summaryKey);
      if (cachedSummary) {
        applyDashboardSummary(cachedSummary);
        return true;
      }

      try {
        const summary = await fetchDashboardSummary(
          apiUrl,
          userId,
          getBasicAuthHeader(),
          { activityLimit: 50 },
        );
        setCache(summaryKey, summary);
        applyDashboardSummary(summary);
        return true;
      } catch (err) {
        if (err?.status === 404) return false;
        return false;
      }
    }

    async function fetchDashboardResultsData() {
      const resultsKey = buildDashboardResultsCacheKey(userId);
      const cachedResults = getCache(resultsKey);
      if (cachedResults) {
        setResultsMap(cachedResults);
        setResultsLoading(false);
        return;
      }

      setResultsLoading(true);
      try {
        const map = await fetchDashboardResults(apiUrl, userId, getBasicAuthHeader());
        setCache(resultsKey, map);
        setResultsMap(map);
      } catch (_) {
        setResultsMap({});
      } finally {
        setResultsLoading(false);
      }
    }

    async function fetchData() {
      let summarySucceeded = false;

      const summaryTask = tryFetchDashboardSummary().then((ok) => {
        summarySucceeded = ok;
        if (ok) setResultsLoading(false);
        return ok;
      });

      const countsTask = loadDashboardCounts();

      // Fast parallel counts — show KPIs without waiting on slow summary API.
      countsTask
        .then((counts) => {
          if (!summarySucceeded) applyDashboardCounts(counts);
          setStatsLoading(false);
        })
        .catch(() => setStatsLoading(false));

      const usedSummary = await summaryTask;
      if (usedSummary) {
        setStatsLoading(false);
      } else {
        try {
          const counts = await countsTask;
          if (!summarySucceeded) applyDashboardCounts(counts);
        } catch (_) {
          // keep defaults
        }
        setStatsLoading(false);
      }

      // Always load the submission results too: the summary endpoint may return
      // 200 with empty avgScore/recentActivity, and these power the Avg-score and
      // Recent-activity sections (with this as the fallback data source).
      fetchDashboardResultsData();
    }

    function extractPackageDetails(package1) {
      if (!package1) return null;
      return {
        numberOfCourses: package1.courses,
        numberOfStudents: package1.students,
        numberOfAssessments: package1.assessments,
      };
    }

    // Legacy package table — limits only; terms flag still read via getPackageDetails().
    // async function fetchPackageDetails() {
    //   const planId = localStorage.getItem('packageDetails');
    //   if (!planId) return;
    //   try {
    //     const packageDetails = await getPackageDetails(planId);
    //     if (!packageDetails) return;
    //     const { numberOfCourses, numberOfStudents, numberOfAssessments } =
    //       extractPackageDetails(packageDetails);
    //     setTotalCourseCount(numberOfCourses);
    //     setTotalStudentCount(numberOfStudents);
    //     setTotalAssessmentCount(numberOfAssessments);
    //     setUserPlan({ numberOfCourses, numberOfStudents, numberOfAssessments, packageDetails });
    //     cachePackageLimits({ numberOfCourses, numberOfStudents, numberOfAssessments, name: packageDetails.name });
    //   } catch (_) { /* keep defaults */ }
    // }

    const checkTermsAcceptance = async () => {
      const planId = localStorage.getItem('packageDetails');
      if (planId) {
        await getPackageDetails(planId);
      }
    };
    checkForPackage();
    checkTermsAcceptance();
  }, [userId]);

  useEffect(() => {
    const checkAndRenewPlan = async () => {
      if (expirationDate && userType!=2) {
        // The authoritative plan state now lives in `instructor_subscription`,
        // surfaced via entitlements. If the user has a resolved tier they hold a
        // valid plan — do NOT fall back to the legacy userplan/Stripe-renewal
        // gate below, whose stale trial rows would otherwise force the plan
        // dialog open (e.g. a 2024 `userplan` row with an empty subscription_id).
        if (entitlements && entitlements.tier) {
          localStorage.setItem('planExpire', "0");
          return;
        }

        const currentDate = new Date();
        const planDate = new Date(expirationDate);

        if (currentDate > planDate) {
          // Only paid plans backed by a real Stripe subscription can be
          // auto-renewed here. Free/trial/legacy rows have no subscription id,
          // so a Stripe renewal call (GET /v1/subscriptions/<empty>) always
          // fails — which must not force the plan dialog open.
          const hasStripeSubscription =
            typeof subscriptionId === 'string' && subscriptionId.startsWith('sub_');
          if (!hasStripeSubscription) {
            localStorage.setItem('planExpire', "0");
            return;
          }
          //console.log('Plan has expired. Checking for renewal...');
          try {
            const body={
              subscriptionId: subscriptionId,
              userId: userId,
            }
            const subscriptionRes = await axios.post(
              `${apiUrl}/api/stripe/subscription-payment`,
              body,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Accept: '*/*',
                  'Access-Control-Allow-Origin': '*',
                  Authorization: getBasicAuthHeader(),
                },
              }
            );

            const data = subscriptionRes.data;
            const currentTimestamp = Math.floor(currentDate.getTime() / 1000);

            if (data.status === 'succeeded' && currentTimestamp < data.period_end) {
              const lastPlanDate = new Date(data.period_start * 1000);
              const nextPlanDate = new Date(data.period_end * 1000);

              // Second API call to update the plan
              const updateRes = await axios.post(
                `${apiUrl}/api/userplans/${userId}`,
                {
                  userId,
                  planId: packageId,
                  price: price1,
                  lastPlanDate: lastPlanDate.toISOString(),
                  nextPlanDate: nextPlanDate.toISOString(),
                  subscriptionId,
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: '*/*',
                    'Access-Control-Allow-Origin': '*',
                    Authorization: getBasicAuthHeader(),
                  },
                }
              );
              //console.log('Plan successfully renewed:', updateRes.data);
              localStorage.setItem('planExpire', "0");
            } else {
              //console.log('Plan expired and not renewed.');
              localStorage.setItem('planExpire', "1");
              if (!sessionStorage.getItem('planRenewDismissed')) {
                setMandatoryPackageDialog(true)
              }
            }
          } catch (error) {
            //console.error('Error during renewal process:', error);
            localStorage.setItem('planExpire', "1");
            if (!sessionStorage.getItem('planRenewDismissed')) {
              setMandatoryPackageDialog(true)
            }
          }
        } else {
          //console.log('Plan is still active.');
          localStorage.setItem('planExpire', "0");
        }
      }
    };

    checkAndRenewPlan();
  }, [expirationDate, entitlements]);

  const getPackageDetails = async (planId) => {
    try {
      const response = await axios.get(`${apiUrl}/api/packages/${planId}/${userId}`, {
        headers: {
          'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
        }
      });
      //console.log(response.data)
      return response.data; // Return only the data
    } catch (error) {
      //console.error('Error fetching package details:', error);
      return null; // Return null if there's an error
    }
  };

  const todayRef = useRef(null);
  const courseDashboardRef = useRef(null);

  // Track visibility of components
  const isTodayInView = useInView(todayRef, { threshold: 0.2 });
  const isCourseDashboardInView = useInView(courseDashboardRef, { threshold: 0.2 });

  const slideInFromLeft = {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  // ---- reference dashboard derived values (all from real API data) ----
  const chartStyle = theme.tweaks?.chart || 'donut';

  // avg score across every graded submission
  const allResults = Object.values(resultsMap).flat();
  const gradedResults = allResults.filter((r) => r.totalMarks && Number(r.totalMarks) > 0);
  const gradedCount = dashboardSummary?.gradedCount ?? gradedResults.length;
  const avgScorePct = dashboardSummary?.avgScorePct != null
    ? dashboardSummary.avgScorePct
    : (gradedResults.length
      ? Math.round(gradedResults.reduce((s, r) => s + (Number(r.score) / Number(r.totalMarks)) * 100, 0) / gradedResults.length)
      : null);

  // recent activity = most recent submissions across all assessments, enriched
  // with role/course/assessment/user so the feed can be filtered. (Submission
  // events come from the result API; admin/audit events would extend this list
  // once an audit-log endpoint is available.)
  const nameById = Object.fromEntries((assessmentList || []).map((a) => [a.testId ?? a.id, a.name]));
  const courseById = Object.fromEntries((assessmentList || []).map((a) => [a.testId ?? a.id, a.courseName]));
  // Prefer the summary's activity, but fall back to the submission results when
  // the summary returns an empty/missing activity list — otherwise the feed is blank.
  const summaryActivity = dashboardSummary
    ? mapDashboardActivity(dashboardSummary.recentActivity, relTime, AV_COLORS)
    : [];
  const resultsActivity = Object.entries(resultsMap)
    .flatMap(([id, rows]) => rows.map((r) => ({ ...r, _name: nameById[id], _course: courseById[id] })))
    .filter((r) => r.createdAt)
    .sort((a, b) => (parseInt(b.createdAt, 10) || 0) - (parseInt(a.createdAt, 10) || 0))
    .map((r, i) => ({
      who: `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.studentEmail || 'Learner',
      role: 'Student',
      action: 'submitted',
      target: r._name || 'an assessment',
      course: r._course || '',
      time: relTime(r.createdAt),
      createdAt: parseInt(r.createdAt, 10) || 0,
      color: AV_COLORS[i % AV_COLORS.length],
      score: (r.totalMarks && Number(r.totalMarks) > 0) ? Math.round((Number(r.score) / Number(r.totalMarks)) * 100) : null,
    }));
  const allActivity = summaryActivity.length > 0 ? summaryActivity : resultsActivity;

  // Filter options + filtered feed for the Recent-activity panel.
  const activityRoleOptions = Array.from(new Set(allActivity.map((a) => a.role)));
  const activityCourseOptions = dashboardSummary?.activityCourses?.length
    ? dashboardSummary.activityCourses
    : Array.from(new Set(allActivity.map((a) => a.course).filter(Boolean)));
  const recentActivity = allActivity
    .filter((a) => activityRole === 'all' || a.role === activityRole)
    .filter((a) => activityCourse === 'all' || a.course === activityCourse)
    .filter((a) => {
      if (activityWindow === 'all') return true;
      const days = parseInt(activityWindow, 10);
      if (!days || !a.createdAt) return true;
      return (Date.now() - a.createdAt) <= days * 86400000;
    })
    .filter((a) => {
      if (!activitySearch.trim()) return true;
      const q = activitySearch.toLowerCase();
      return `${a.who} ${a.target} ${a.course}`.toLowerCase().includes(q);
    })
    .slice(0, 12);

  // 6-month activity trend (submissions vs passed) from real result timestamps
  const trendData = dashboardSummary?.trend
    ? mapDashboardTrend(dashboardSummary.trend)
    : buildTrend(allResults);

  const overview = [
    { label: 'Courses', used: courseCount, total: totalCourseCount },
    { label: 'Assessments', used: assessmentCount, total: totalAssessmentCount },
    { label: 'Learners', used: studentCount, total: totalStudentCount },
  ];
  const stats = [
    { label: 'Learners', value: studentCount, total: totalStudentCount || 1, icon: 'enroll', meta: 'of plan limit', help: DASHBOARD_KPI_HELP.learners },
    { label: 'Active courses', value: courseCount, total: totalCourseCount || 1, icon: 'course', countOnly: true, meta: 'total', help: DASHBOARD_KPI_HELP.activeCourses },
    { label: 'Assessments', value: assessmentCount, total: totalAssessmentCount || 1, icon: 'assess', countOnly: true, meta: 'total', help: DASHBOARD_KPI_HELP.assessments },
    { label: 'Avg. score', value: avgScorePct != null ? avgScorePct : '—', suffix: avgScorePct != null ? '%' : '', total: 100, icon: 'target', meta: `across ${gradedCount} graded`, help: `${DASHBOARD_KPI_HELP.avgScore}${gradedCount ? ` (${gradedCount} graded so far).` : '.'}` },
  ];
  const trainingStats = dashboardSummary
    ? {
      total: dashboardSummary.trainingCount ?? 0,
      completed: dashboardSummary.trainingCompleted ?? 0,
      pct: dashboardSummary.trainingCount
        ? Math.round(((dashboardSummary.trainingCompleted ?? 0) / dashboardSummary.trainingCount) * 100)
        : 0,
    }
    : (() => {
    const total = (trainings || []).length;
    const now = Date.now();
    const completed = (trainings || []).filter((t) => {
      const c = t.completionDate ? new Date(t.completionDate).getTime() : null;
      return c && c <= now;
    }).length;
    const pct = total ? Math.round((completed / total) * 100) : 0;
    return { total, completed, pct };
  })();

  // Greeting follows the viewer's local time zone (browser local hours).
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  return (
    <>
    <DashboardNavbar menuItems={menuItems} />
    <ConWrapper>
      <ContainerDashboard>
        <PackageDialog open={dialogOpen} handleClose={() => setDialogOpen(false)} />
        <MandatoryPackageDialog
          open={mandatoryPackageDialog && canAccessBilling(entitlements)}
          expiredDate={expirationDate}
          onDismiss={() => {
            setMandatoryPackageDialog(false);
            sessionStorage.setItem('planRenewDismissed', '1');
          }}
          onActivationRedeemed={() => {
            setMandatoryPackageDialog(false);
            localStorage.setItem('planExpire', '0');
            window.location.reload();
          }}
        />
        <PlanExpiredNoticeDialog
          open={mandatoryPackageDialog && isNonOwnerOrgStaff(entitlements)}
          expiredDate={expirationDate}
          onDismiss={() => {
            setMandatoryPackageDialog(false);
            sessionStorage.setItem('planRenewDismissed', '1');
          }}
        />
        <DashboardContainer>
      {snackbarOpen && !dontShowAgain && canShowStripeConnect(entitlements) && (
        <StripeSetupBanner role="alert">
          <StripeBannerBody>
            <StripeBannerMessage>{snackbarMessage}</StripeBannerMessage>
            <StripeBannerToggleRow>
              <Toggle
                checked={dontShowAgain}
                label="Don't show this screen on startup again."
                onChange={(v) => handleCheckboxChange({ target: { checked: v } })}
              />
            </StripeBannerToggleRow>
          </StripeBannerBody>
          <StripeBannerActions>
            <Btn
              onClick={() => guardAction('action.dashboard.stripeSetup', handleStripeOnboarding)}
              disabled={isLoading}
            >
              {isLoading ? <ButtonSpinnerSlot size={16} /> : 'Setup Stripe account'}
            </Btn>
            <IconBtn
              type="button"
              onClick={handleSnackbarClose}
              title="Dismiss"
              aria-label="Dismiss Stripe setup notice"
            >
              <X size={16} />
            </IconBtn>
          </StripeBannerActions>
        </StripeSetupBanner>
      )}
      <DashboardHero>
        <div>
          <HeroTitle>{greeting}{username ? `, ${username}` : ""}</HeroTitle>
          <HeroText>
            Track course capacity, assessment usage, and enrollments from your current plan.
          </HeroText>
        </div>
        <HeroActions>
          <Seg>
            {['This week', 'This term', 'All time'].map((p) => (
              <button
                key={p}
                className={period === p ? 'is-on' : ''}
                onClick={() => guardAction('action.dashboard.periodFilter', () => setPeriod(p))}
              >
                {p}
              </button>
            ))}
          </Seg>
          <IconBtn
            onClick={() => guardAction('action.portal.themeToggle', toggleModeFromEvent)}
            title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </IconBtn>
          {canAccessBilling(entitlements) && (
            <Btn
              onClick={handleDialogOpen}
              $variant={isLocked('action.dashboard.upgradePlan') ? 'ghost' : undefined}
            >
              {isLocked('action.dashboard.upgradePlan') ? (
                <Lock size={15} strokeWidth={2} aria-hidden="true" />
              ) : null}
              Upgrade Plan
            </Btn>
          )}
          <Btn
            $variant="ghost"
            onClick={() => guardAction('action.dashboard.newCourse', () => navigate('/Portal/Instructor/Course'))}
          >
            {isLocked('action.dashboard.newCourse') ? (
              <Lock size={15} strokeWidth={2} aria-hidden="true" />
            ) : (
              <Icon name="plus" size={16} />
            )}
            New Course
          </Btn>
        </HeroActions>
      </DashboardHero>

      {statsLoading ? (
        <StatGrid>
          {[0, 1, 2, 3].map((i) => (
            <Stat key={i}>
              <StatTop>
                <Skeleton $w="38px" $h="38px" $r={theme.rMd} />
                <Skeleton $w="44px" $h="18px" $r="999px" />
              </StatTop>
              <Skeleton $w="55%" $h="11px" $mb="10px" style={{ marginTop: 12 }} />
              <Skeleton $w="42%" $h="22px" $mb="12px" />
              <Skeleton $w="100%" $h="6px" $r="999px" $mb="10px" />
              <Skeleton $w="60%" $h="10px" />
            </Stat>
          ))}
        </StatGrid>
      ) : (
        <StatGrid>
          {stats.map((s) => (
            <Stat key={s.label}>
              <StatTop>
                <StatIco><Icon name={s.icon} /></StatIco>
                {s.delta && <StatDelta $dir={s.up ? 'up' : 'down'}><Icon name={s.up ? 'up' : 'down'} size={13} />{s.delta}</StatDelta>}
              </StatTop>
              <StatLabel>
                <LabelWithHelp help={s.help}>{s.label}</LabelWithHelp>
              </StatLabel>
              <StatValue className="tnum">
                {s.value}{s.suffix || ''}{!s.suffix && !s.countOnly && typeof s.value === 'number' && <small> / {s.total}</small>}
              </StatValue>
              {!s.countOnly && <StatBar><i style={{ width: `${Math.min((s.value / s.total) * 100, 100)}%` }} /></StatBar>}
              <StatMeta>{s.meta}</StatMeta>
            </Stat>
          ))}
        </StatGrid>
      )}

      {statsLoading ? (
        <Card $pad style={{ marginTop: theme.gap }}>
          <div style={{ marginBottom: 14 }}>
            <Skeleton $w="180px" $h="14px" $mb="8px" />
            <Skeleton $w="260px" $h="11px" />
          </div>
          <Skeleton $w="100%" $h="12px" $r="999px" />
        </Card>
      ) : (
        <Card $pad style={{ marginTop: theme.gap }}>
          <RowBetween style={{ marginBottom: 14 }}>
            <div>
              <ChartTitle>Training activities</ChartTitle>
              <ChartHint>Overall training completion across your programs</ChartHint>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <LegendRow style={{ fontSize: theme.fsMicro }}><LegendSw style={{ background: theme.accent }} />Completed</LegendRow>
              <LegendRow style={{ fontSize: theme.fsMicro }}><LegendSw style={{ background: `color-mix(in oklch, ${theme.accent} 20%, ${theme.surface3})` }} />Remaining</LegendRow>
            </div>
          </RowBetween>
          <RowBetween style={{ marginBottom: 6 }}>
            <span style={{ fontSize: theme.fsSm, fontWeight: 600 }}>
              {trainingStats.completed} of {trainingStats.total} trainings complete
            </span>
            <span className="tnum" style={{ fontSize: theme.fsSm, fontWeight: 700 }}>{trainingStats.pct}%</span>
          </RowBetween>
          <div style={{ height: 12, borderRadius: 999, background: `color-mix(in oklch, ${theme.accent} 18%, ${theme.surface3})`, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${trainingStats.pct}%`, background: theme.accent, borderRadius: 999, transition: 'width .35s ease' }} />
          </div>
        </Card>
      )}

      {resultsLoading ? (
        <ChartGrid style={{ marginTop: theme.gap }}>
          <Card $pad>
            <Skeleton $w="160px" $h="14px" $mb="8px" />
            <Skeleton $w="220px" $h="11px" $mb="18px" />
            <Skeleton $w="100%" $h="180px" $r={theme.rMd} />
          </Card>
          <Card $pad>
            <Skeleton $w="120px" $h="14px" $mb="8px" />
            <Skeleton $w="180px" $h="11px" $mb="18px" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <Skeleton $circle $h="120px" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Skeleton $w="80%" $h="12px" />
                <Skeleton $w="70%" $h="12px" />
                <Skeleton $w="75%" $h="12px" />
              </div>
            </div>
          </Card>
        </ChartGrid>
      ) : (
        <ChartGrid style={{ marginTop: theme.gap }}>
          <Card $pad>
            <RowBetween style={{ marginBottom: 18 }}>
              <div>
                <ChartTitle>Course activities</ChartTitle>
                <ChartHint>Submissions vs. passes, last 6 months</ChartHint>
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <LegendRow style={{ fontSize: theme.fsMicro }}><LegendSw style={{ background: theme.accent }} />Submissions</LegendRow>
                <LegendRow style={{ fontSize: theme.fsMicro }}><LegendSw style={{ background: `color-mix(in oklch, ${theme.accent} 38%, ${theme.surface3})` }} />Passed</LegendRow>
              </div>
            </RowBetween>
            <StackBars data={trendData} />
          </Card>

          <Card $pad>
            <ChartTitle>Plan usage</ChartTitle>
            <ChartHint>Learner capacity{(userPlan && userPlan.packageDetails && userPlan.packageDetails.name) ? ` · ${userPlan.packageDetails.name}` : ''}</ChartHint>
            {chartStyle === 'donut' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 14 }}>
                <Donut value={overview[2].used} total={overview[2].total || 1} />
                <Legend style={{ flex: 1 }}>
                  {overview.map((o, i) => (
                    <LegendRow key={o.label}>
                      <LegendSw style={{ background: [theme.accent, `color-mix(in oklch, ${theme.accent} 55%, ${theme.surface3})`, `color-mix(in oklch, ${theme.accent} 28%, ${theme.surface3})`][i] }} />
                      {o.label}<b className="tnum">{o.used}/{o.total}</b>
                    </LegendRow>
                  ))}
                </Legend>
              </div>
            ) : (
              <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {overview.map((o) => (
                  <div key={o.label}>
                    <RowBetween style={{ marginBottom: 6 }}>
                      <span style={{ fontSize: theme.fsSm, fontWeight: 600 }}>{o.label}</span>
                      <Muted className="tnum" style={{ fontSize: theme.fsSm }}>{o.used} / {o.total}</Muted>
                    </RowBetween>
                    <StatBar style={{ height: 8 }}><i style={{ width: `${o.total ? (o.used / o.total) * 100 : 0}%` }} /></StatBar>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </ChartGrid>
      )}

      <div style={{ marginTop: theme.gap }}>
        <SectionHead style={{ marginTop: 0 }}>
          <div><h2>Recent activity</h2><p>Live feed across your workspace — filter by role, course, assessment or user</p></div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={activityRole}
              onChange={(e) => guardAction('action.dashboard.activityFilters', () => setActivityRole(e.target.value))}
              style={{ height: 32, padding: '0 10px', borderRadius: theme.rMd, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text, fontSize: theme.fsSm, outline: 'none' }}
            >
              <option value="all">All roles</option>
              {activityRoleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select
              value={activityCourse}
              onChange={(e) => guardAction('action.dashboard.activityFilters', () => setActivityCourse(e.target.value))}
              style={{ height: 32, padding: '0 10px', borderRadius: theme.rMd, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text, fontSize: theme.fsSm, outline: 'none', maxWidth: 180 }}
            >
              <option value="all">All courses</option>
              {activityCourseOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={activityWindow}
              onChange={(e) => guardAction('action.dashboard.activityFilters', () => setActivityWindow(e.target.value))}
              style={{ height: 32, padding: '0 10px', borderRadius: theme.rMd, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text, fontSize: theme.fsSm, outline: 'none' }}
            >
              <option value="all">Any time</option>
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
            </select>
            <input
              value={activitySearch}
              onChange={(e) => guardAction('action.dashboard.activityFilters', () => setActivitySearch(e.target.value))}
              placeholder="Search user or assessment…"
              style={{ height: 32, padding: '0 12px', borderRadius: theme.rMd, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text, fontSize: theme.fsSm, outline: 'none', width: 200 }}
            />
          </div>
        </SectionHead>
        <Card $pad style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {resultsLoading ? (
            [0, 1, 2, 3, 4].map((i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '11px 0', borderBottom: i < 4 ? `1px solid ${theme.border}` : 'none' }}>
                <Skeleton $circle $h="30px" />
                <div style={{ flex: 1 }}>
                  <Skeleton $w="72%" $h="12px" $mb="8px" />
                  <Skeleton $w="40%" $h="10px" />
                </div>
                <Skeleton $w="36px" $h="22px" $r="999px" />
              </div>
            ))
          ) : (
            <>
              {recentActivity.length === 0 && (
                <Muted style={{ fontSize: theme.fsSm, padding: '14px 0', textAlign: 'center' }}>No activity matches these filters.</Muted>
              )}
              {recentActivity.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '11px 0', borderBottom: i < recentActivity.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                  <Av style={{ background: a.color, width: 30, height: 30 }}>{a.who === 'You' ? '★' : initials(a.who)}</Av>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: theme.fsSm, lineHeight: 1.45 }}>
                      <b>{a.who}</b> <Muted>{a.action}</Muted> <b style={{ fontWeight: 600 }}>{a.target}</b>
                      {a.course ? <Muted> · {a.course}</Muted> : null}
                    </div>
                    <div style={{ fontSize: theme.fsMicro, color: theme.textSubtle, marginTop: 2 }}>
                      <Tag $tone="gray" style={{ marginRight: 6 }}>{a.role}</Tag>{a.time}
                    </div>
                  </div>
                  {a.score != null && <Tag $tone="accent" className="tnum" style={{ alignSelf: 'center' }}>{a.score}%</Tag>}
                </div>
              ))}
            </>
          )}
        </Card>
      </div>
    </DashboardContainer>
      </ContainerDashboard>
    </ConWrapper>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
  //<div style={{ marginTop: '20px' }}>
  //        {userPlan && <Today userplan={userPlan} />}
  //      </div>
  //{/* CourseDashboard Component */}
  //<div ref={courseDashboardRef}>
  //<motion.div
  //  initial="hidden"
  //  animate={isCourseDashboardInView ? "visible" : "hidden"}
  //  variants={slideInFromRight}>
  //  <div style={{ ...componentStyle, ...hoverEffect }}>
  //    {course && <CourseDashboard course={course} />}
  //  </div>
  //</motion.div>
//</div>
}
